/**
 * Double-submit-cookie CSRF utilities (OCP-125).
 *
 * Same code-path used by api + admin + customer-portal — the token is
 * generated on the server (login), shipped to the browser as a
 * non-HttpOnly cookie, and replayed by JS in the `X-CSRF-Token` header
 * on every state-changing request. The server validates that header
 * against the cookie with a constant-time compare. Match = legitimate
 * call from a frontend that holds the cookie. Mismatch / missing =
 * cross-site request that can read neither the cookie nor inject the
 * header (browser SOP prevents JS on a foreign origin from reading
 * cookies on `*.orboto.io`).
 *
 * Why double-submit (stateless) instead of synchronizer-token
 * (server-stored): no server lookup per request, no race between
 * concurrent tabs, scales without an extra DB hit. The trade-off is
 * the cookie must be unreadable by foreign-origin JS — which it is,
 * because SOP enforces same-site cookie reads for non-HttpOnly cookies
 * exactly the way it does for HttpOnly cookies (HttpOnly only blocks
 * JS access from the SAME origin; cross-origin JS never gets to read
 * any cookie regardless).
 *
 * Cookie naming mirrors the session cookies:
 *   - operator surface: `__Host-orboto_csrf` next to `__Host-orboto_session`
 *   - portal surface:  `__Host-orboto_portal_csrf` next to
 *     `__Host-orboto_portal_session`
 *
 * OCP-924 - the `__Host-` prefix makes the browser refuse the cookie unless
 * it is Secure, Path=/ and carries NO Domain attribute. So it lives on the
 * one host that set it (account.orboto.io / admin.orboto.io), and no
 * `*.orboto.io` sibling can plant ("toss") a same-named cookie with a value
 * it knows - the residual hole of double-submit CSRF when the cookie was
 * `Domain=.orboto.io`.
 */

/** HTTP header the frontend uses to replay the cookie value. */
export const CSRF_HEADER_NAME = 'x-csrf-token';

/** Cookie holding the CSRF token on the operator surface. */
export const OPERATOR_CSRF_COOKIE_NAME = '__Host-orboto_csrf';

/** Cookie holding the CSRF token on the customer-portal surface. */
export const PORTAL_CSRF_COOKIE_NAME = '__Host-orboto_portal_csrf';

/** Token length — 32 bytes ≈ 256 bits of entropy, encoded as 64 hex chars. */
export const CSRF_TOKEN_BYTES = 32;

/**
 * Generate a random CSRF token. Node + modern browsers both expose
 * `crypto.getRandomValues` on the global `crypto` object — this helper
 * works on the server (Node 20+) AND in the bundle without pulling in
 * `node:crypto` (which Vite would have to polyfill).
 */
export function generateCsrfToken(bytes: number = CSRF_TOKEN_BYTES): string {
  const buf = new Uint8Array(bytes);
  // crypto.getRandomValues is universal: Node 20+ exposes it on the
  // global Web Crypto object; browsers have had it for a decade.
  crypto.getRandomValues(buf);
  return bytesToHex(buf);
}

/**
 * Constant-time string comparison. Avoids leaking via wall-clock
 * timing whether a forged token matched the first N characters of the
 * real one. Lengths-differ short-circuit IS observable timing-wise but
 * the length of a CSRF cookie is not secret — only the contents are.
 */
export function constantTimeEqual(a: string, b: string): boolean {
  if (a.length !== b.length) return false;
  let mismatch = 0;
  for (let i = 0; i < a.length; i++) {
    mismatch |= a.charCodeAt(i) ^ b.charCodeAt(i);
  }
  return mismatch === 0;
}

/**
 * Validate a header against a cookie. Returns false when either is
 * missing or zero-length; only `true` when both are present, equal
 * length, and constant-time-match.
 */
export function validateCsrfToken(
  headerToken: string | undefined | null,
  cookieToken: string | undefined | null,
): boolean {
  if (!headerToken || !cookieToken) return false;
  if (headerToken.length === 0 || cookieToken.length === 0) return false;
  return constantTimeEqual(headerToken, cookieToken);
}

function bytesToHex(bytes: Uint8Array): string {
  let out = '';
  for (let i = 0; i < bytes.length; i++) {
    out += bytes[i].toString(16).padStart(2, '0');
  }
  return out;
}
